/**
 * Draglist Class
 * issue: https://rituals.atlassian.net/browse/DL-12313
 *
 * Creates a draggable horizontal list of draggable items
 */
'use strict';

const gtm = require('./modules/google').tagManager;
const debounce = require('./modules/helpers/debounce');
const equalHeight = require('./modules/helpers/equalHeight');
const prepareDeferedImages = require('./modules/helpers/prepareDeferedImages');
const responsiveBg = require('./responsive-background');
const isMobileDevice = require('./modules/helpers/isMobileDevice');
const isTouchDevice = require('./modules/helpers/isTouchDevice');
const damHelper = require('./dam/dam-helper');
const productGrid = require('./product-grid');
const advent = require('./advent');
const tileSelectors = ['.drag-list-body .js-drag-list-item'];

const dragListEl = '.js-drag-list';
const dragListListItemEl = '.js-drag-list-item';
const dragListListItemActiveEl = 'drag-list-item-active';
const dragListControlPrevEl = '.js-drag-list-prev';
const dragListControlNextEl = '.js-drag-list-next';
const PUSHED_CLASS = 'js-data-layer-list-pushed';

let shouldPreventDefault = false;
let isMouseDown = false;
let activeSlide;
let parentWidth = 0;

const dragListElements = [];
const dragListState = {
    'rect': '',
    'startX': 0,
    'clientX': 0,
    'target': '',
};

const dragListDefaults = {
    'horizontalPadding': isMobileDevice() ? 50 : 120,
    'horizontalTileMargin': isMobileDevice() ? 10 : 20,
    'maxTileWidthDesktop': 409,
    'itemsToShow': 4.5,
    'itemsToScroll': 4,
    'responsive': [
        {
            'breakpoint': 1300,
            'settings': {
                'itemsToShow': 3.5,
                'itemsToScroll': 3
            }
        }, {
            'breakpoint': 1100,
            'settings': {
                'itemsToShow': 2.5,
                'itemsToScroll': 3
            }
        }
    ]
};

/**
 * Prepares the markup for the drag list to work
 */
const prepareMarkup = () => {
    // Wrap list in class set calculated width inline.
    $(dragListEl).each((i, el) => {
        if ($(el).hasClass('draglist-prepared')) {
            return false;
        }

        damHelper.updateImages(el);

        if (shouldLazyLoad(el)) {
            initLazyLoad(el);
        } else {
            prepareDeferedImages(el);
        }

        if (getProp(el, 'hideArrows') || !shouldPrepareMarkup(el)) {
            $(el).next().addClass('visually-hidden');
        }

        if (typeof getProp(el, 'horizontalPadding') !== 'undefined') {
            dragListDefaults.horizontalPadding = parseInt(getProp(el, 'horizontalPadding'));
        }

        if (typeof getProp(el, 'horizontalTileMargin') !== 'undefined') {
            dragListDefaults.horizontalTileMargin = parseInt(getProp(el, 'horizontalTileMargin'));
        }

        if (getProp(el, 'hideBar')) {
            $(el).addClass('hidden-bar');
        }

        if (shouldPrepareMarkup(el)) {
            if ($(el).parents('.js-advent-overlay-inner').find('.js-advent-slider-image').length){
                $(el).parents('.js-advent-overlay-inner').find('.js-advent-slider-image').each(function () {
                    prepAdventSliderImg($(this));
                });
            }

            setParentWidth(el);
            setDraglistWidth(el);
            equalHeight($('.product-details-content', el));
            responsiveBg.init();

            if ($(el).hasClass('js-slider-advent')) {
                const $adventImage = $(el).parents('.js-advent-overlay-inner').find('.js-advent-slider-image');
                const imgHeight = $adventImage[0].naturalHeight;
                const track = $(el).find('.drag-list-body');
                const imgSrc = $adventImage.attr('src');
                track.css({
                    'height': imgHeight,
                    'background-image': 'url(' + imgSrc + ')'
                });
            }
        } else {
            $(el).css({'visibility': 'visible'});
            $(el).next().css({'visibility': 'visible'});
        }

        $(el).addClass('draglist-prepared');
    });
};

/**
 * Gets the width of a draglist tile based on 'itemsToShow' prop
 *
 * @param {DOM Element} el The draglist DOM element
 * @returns {Float} Width of tile
 */
const getDraglistTileWidth = (el) => {
    const p = dragListDefaults.horizontalPadding > 0 ? dragListDefaults.horizontalPadding / 2 : 0;
    const w = (parentWidth - p) / getProp(el, 'itemsToShow');
    return Math.min(w, dragListDefaults.maxTileWidthDesktop);
};

/**
 * Sets the width of a certain dragList element
 *
 * @param {DOM Element} el The draglist DOM element
 */
const setDraglistWidth = (el) => {
    let width = 0;
    const hasListBody = $('.drag-list-body', el).length;

    if (hasListBody) {
        $('.drag-list-body', el).contents().unwrap();
    }

    $(el).children().each((i, child) => {
        if (!isMobileDevice() && !$(el).hasClass('js-slider-advent')) {
            const w = getDraglistTileWidth(el);
            $(child).width(w);
        }

        width += $(child).width();
    });

    width += dragListDefaults.horizontalPadding;
    $(el).addClass('init').wrapInner(`<div class="drag-list-body" style="min-width: ${width}px;"></div>`);
};

const setParentWidth = (el) => {
    parentWidth = el.parentNode.getBoundingClientRect().width;
};

/**
 * Resizes the a certain dragList element
 *
 * @param {DOM Element} el The draglist DOM element
 */
const resizeDraglist = (el) => {
    let width = 0;

    $('.drag-list-body', el).children().each((i, child) => {
        if (!isMobileDevice() && !$(el).hasClass('js-slider-advent')) {
            const w = getDraglistTileWidth(el);
            $(child).width(w);
        }

        width += $(child).width();
    });

    width += dragListDefaults.horizontalPadding;
    $('.drag-list-body', el).css({
        'min-width': width + 'px'
    });
};

/*
 * Inits the custom scrollbar logic
 */
const initDragList = () => {
    $(dragListEl).each((i, el) => {
        const $dragListScrollEl = $(el);

        if ($dragListScrollEl.hasClass('draglist-initialized')) {
            return false;
        }

        if (getProp(el, 'isMobileOnly')) {
            // To keep draglistElements array length consistant with number on page
            dragListElements.push('isMobileOnly');
            initAdventConceptHighlightBlockEvents(el);
            $dragListScrollEl.addClass('draglist-initialized');
            return true;
        }

        // Store instance in array.
        dragListElements.push(el);

        // Store index of list to be able to grab the correct one later.
        $dragListScrollEl.data('listIndex', i);

        // Handle radiobutton in list tile
        $dragListScrollEl.find('input[type="radio"]').on('click', {'currentList': el}, onClickRadio);

        // Disable dragging of links and images.
        $dragListScrollEl.find('a, img').attr('draggable', false);

        $dragListScrollEl.on('mousedown', onMouseDown);
        $dragListScrollEl.on('scroll', debounce((e) => { updateControls(e.currentTarget); }, 100));
        $dragListScrollEl.on('scroll', debounce(() => { productGrid.pushProductsToDataLayer(tileSelectors); }, 100));

        // Extend current click behavior set in App.js.
        // Normally we would use the a element.
        $dragListScrollEl.find('.js-linked-block').on('click.dragList', onClick);

        $dragListScrollEl.next().find(dragListControlPrevEl).on('click', {'listIndex': i}, onClickPrev);
        $dragListScrollEl.next().find(dragListControlNextEl).on('click', {'listIndex': i}, onClickNext);
        $dragListScrollEl.addClass('draglist-initialized');

        if (isMobileDevice() && getProp(el, 'useEffect') === 'magnify') {
            $dragListScrollEl.addClass('effect-magnify');
            $dragListScrollEl.off('scroll').on('scroll', () => { onTouchMove(el); });
        }
    });

    $(document).on('mouseup.dragList', onMouseUp);
    $(document).on('mouseup.dragList', debounce(() => { productGrid.pushProductsToDataLayer(tileSelectors); }, 100));
    $(document).on('mousemove.dragList', onMouseMove);
    $(window).on('resize.dragList', debounce(() => { onResize(); }, 200));
};

function initAdventConceptHighlightBlockEvents(el) {
    if (!advent.getAdventDataLayerDataWithActiveProductImageSlide()) {
        return;
    }

    if (isMobileDevice()) {
        $(el).off('scroll').on('scroll', () => { onTouchMove(el, handleAdventDataLayerPush); });
    }
}

function prepAdventSliderImg(img){
    // Prepare slider block
    const fullSlideWidth = parentWidth;
    let imgHeight = img[0].naturalHeight;
    let imgWidth = img[0].naturalWidth;

    // if image is larger than the viewport in height
    if (imgHeight > ($(window).height() * 0.7)){
        imgHeight = $(window).height() * 0.7;
        imgWidth = imgHeight * img[0].naturalWidth / img[0].naturalHeight;
    }

    // calculate amount of full slides
    let fullSlidesAmount = Math.floor(imgWidth / fullSlideWidth) || 1;

    // if fullscreen slides are even, create one less.
    // We need an odd number of slides in order to start slider in the middle
    if (fullSlidesAmount % 2 == 0){
        fullSlidesAmount = fullSlidesAmount - 1;
    }

    // calculate edge slides widths (should always be smaller that the fullwidth ones)
    const edgeSlideWidthDecimal = (imgWidth - (fullSlidesAmount * fullSlideWidth)) / 2;
    const edgeSlideWidth = Math.round(edgeSlideWidthDecimal);

    const totalSlidesCount = fullSlidesAmount + (edgeSlideWidth > 0 ? 2 : 0); // amount of fullscreenslides + edges

    const adventSliderEl = img.closest('.js-advent-overlay').find('.js-slider-advent');

    // attach the empty slide divs
    for (let i = 0; i < totalSlidesCount; i++) {
        adventSliderEl.append($('<div class="advent-slide js-drag-list-item"></div>'));
    }

    const slide = adventSliderEl.find('.js-drag-list-item');

    // give correct widths to the slides
    slide.each(function() {
        if ($(this).is(':first-child') || $(this).is(':last-child')) {
            $(this).css('width', edgeSlideWidth);
        } else {
            $(this).css('width', fullSlideWidth);
        }
    });
}

const calculateScaledTranslatePct = (i, el, min, max) => {
    const {left, right, width} = el.getBoundingClientRect();

    // Clear active class
    $(el).removeClass(dragListListItemActiveEl);

    // Items inside viewport
    if ((left > min && left < max) || (right > min && right < max)) {
        const visiblePx = (left < min) ? right - min : max - left;
        return percentageStepper(0.2, (100 / width) * visiblePx);
    }

    return null;
};

/**
 * Evaluates a draglist item and potentially evaluates it when in viewport
 *
 * @param {Number} i The number of the slide in the draglist element
 * @param {DOM Element} el The element to evaluate and magnify
 * @param {Number} min The minimum postion to start evaluating an element
 * @param {Number} max The maximum position to stop evaluating an element
 */
const setMagnification = (i, el, min, max) => {
    const scaledTranslatePct = calculateScaledTranslatePct(i, el, min, max);
    if (scaledTranslatePct) {
        const scaledTranslateY = (-1 * 20 * scaledTranslatePct);
        el.setAttribute('style', 'transform: translateY(' + scaledTranslateY + 'px);');
        if (scaledTranslatePct === 1) {
            $(el).addClass(dragListListItemActiveEl);
            handleDataLayerPush(i, el);
        }
    }
};

/**
 * Converts the given float to the nearest 0.1, 0.2, ... 0.9, 1
 *
 * @param {Float} min Minimum return value
 * @param {Float} val The percentage (between 0 ... 1)
 * @returns {Float} New number or minimum value
 */
const percentageStepper = (min, val) => {
    const step = (Math.round(Math.ceil(val) / 10) * 10) / 100;
    return step < min ? min : (step > 1) ? 1 : step;
};

/**
 * Handles the datalayer push of element in view
 *
 * @param {Number} i Index of element in view
 * @param {DOM Object} el DOM element in view
 */
const handleDataLayerPush = (i, el) => {
    const $tileToPush = $(el);
    if (i !== activeSlide && !$tileToPush.hasClass(PUSHED_CLASS)) {
        activeSlide = i;

        const datalayerProduct = $('.js-product-tile-datalayer-product', $tileToPush);

        if (datalayerProduct.length > 0) {
            const productData = JSON.parse(datalayerProduct.val());

            if (productData) {
                gtm.pushProductTileView(productData);
            }
        }

        $tileToPush.addClass(PUSHED_CLASS);
    }
};

const handleAdventDataLayerPush = (i, el, min, max) => {
    const scaledTranslatePct = calculateScaledTranslatePct(i, el, min, max);
    if (scaledTranslatePct !== 1) {
        return;
    }

    advent.pushAdventDataLayerDataWithActiveProductImageSlide('feature slider');
};

/**
 * Handles lazyLoading of all images inside the individual tiles
 *
 * @param {HTML Element} el Draglist element
 */
const initLazyLoad = (el) => {
    const observer = new IntersectionObserver((blocks) => {
        for (const block of blocks) {
            const $el = $(block.target);

            if (block.isIntersecting === true && !$el.hasClass('lazyloaded')) {
                $el.addClass('lazyloaded');
                prepareDeferedImages($el);
            }
        }
    }, {
        threshold: [0.05]
    });

    observer.observe(el);
};

/**
 * Updates the previous and next button once the scrollbar hits the beginning
 * or the end.
 *
 * @param {HTML Element} el The HTML Element to update
 */
const updateControls = (el) => {
    const isMaxScroll = el.scrollLeft === (el.scrollWidth - el.clientWidth);
    const isMinScroll = el.scrollLeft === 0;

    // Show / hide controls based on scollLeft.
    $(el).closest(dragListEl).toggleClass('left-gradient', !isMinScroll);
    $(el).closest(dragListEl).next().find(dragListControlPrevEl).toggleClass('hidden', isMinScroll);
    $(el).closest(dragListEl).toggleClass('right-gradient', !isMaxScroll);
    $(el).closest(dragListEl).next().find(dragListControlNextEl).toggleClass('hidden', isMaxScroll);
};

/**
 * Sets all the variables needed to be able to move the slider
 *
 * @param {Event} e Mouse down event
 */
const onMouseDown = (e) => {
    const listIndex = $(e.currentTarget).data('listIndex');
    const $dragListScrollEl = $(dragListElements[listIndex]);

    // Setup draglistState using current list.
    dragListState.target = e.currentTarget;
    dragListState.rect = dragListState.target.getBoundingClientRect();
    dragListState.startX = $dragListScrollEl.scrollLeft() + e.clientX - dragListState.rect.x;
    dragListState.clientX = e.clientX;
    isMouseDown = true;
};

/**
 * Resets all the variable to their initial value
 *
 * @param {Event} e Mouse up event
 */
const onMouseUp = () => {
    // Reset state for later use
    dragListState.target = '';
    dragListState.rect = '';
    dragListState.startX = 0;
    dragListState.clientX = 0;
    isMouseDown = false;
};

/**
 * Moves the slider
 *
 * @param {Event} e Mouse move event
 */
const onMouseMove = (e) => {
    if (isMouseDown) {

        // Calculate actual pixels to move dragList instance by
        const dragListX = dragListState.startX - (e.clientX - dragListState.rect.x);

        // Calculate actual moved pixels by pointer
        const deltaX = dragListState.clientX - e.clientX;

        // Determine drag list scroll element.
        const listIndex = $(dragListState.target).data('listIndex');
        const $dragListScrollEl = $(dragListElements[listIndex]);

        $dragListScrollEl.scrollLeft(dragListX);
        shouldPreventDefault = (deltaX > 2 || deltaX < -2);
    }
};

/**
 * Handles the scroll event on mobile
 *
 * @param {DOM Object} el DOM element to magnify
 */
const onTouchMove = (el, dataLayerPushFunction) => {
    const min = el.getBoundingClientRect().left;
    const max = el.getBoundingClientRect().right;
    const children = el.querySelectorAll(dragListListItemEl);

    children.forEach((child, index) => {
        if (dataLayerPushFunction && $.isFunction(dataLayerPushFunction)) {
            dataLayerPushFunction(index, child, min, max);
            return;
        }
        setMagnification(index, child, min, max);
    });
};

/**
 * Makes sure no click is triggered right after moving
 *
 * @param {Event} e Click event
 */
const onClick = (e) => {
    if (shouldPreventDefault) {
        shouldPreventDefault = false;
        e.preventDefault();
        return false;
    }
};

/**
 * Moves list backwards
 *
 * @param {Event} e Click event
 */
const onClickPrev = (e) => {
    const visibleItemIndex = getFirstVisibleItem(e.data.listIndex);
    const dragListScrollEl = dragListElements[e.data.listIndex];
    const listItemWidth = $(dragListScrollEl).find(dragListListItemEl).width();
    const isOutOfRange = visibleItemIndex <= getProp(dragListScrollEl.el || dragListScrollEl , 'itemsToScroll');

    // Calculate next item to scroll to
    const newIndex = visibleItemIndex - getProp(dragListScrollEl.el || dragListScrollEl, 'itemsToScroll');
    const newScrollPos = isOutOfRange ? 0 : listItemWidth * newIndex;

    $(dragListScrollEl).animate({scrollLeft: newScrollPos}, 500, 'easeOutQuad');
    handleGTMScrollEvent(dragListScrollEl, 'left');
};

/**
 * Moves list forwards
 *
 * @param {Event} e Click event
 */
const onClickNext = (e) => {
    const visibleItemIndex = getFirstVisibleItem(e.data.listIndex);
    const dragListScrollEl = dragListElements[e.data.listIndex];
    const listItemWidth = $(dragListScrollEl).find(dragListListItemEl).width();
    const maxScroll = dragListScrollEl.scrollWidth - dragListScrollEl.clientWidth;

    // Calculate next item to scroll to.
    const itemsToScroll = parseInt(getProp(dragListScrollEl.el || dragListScrollEl, 'itemsToScroll'));
    const newIndex = visibleItemIndex + itemsToScroll;
    const isOutOfRange = (listItemWidth * newIndex) >= maxScroll;
    const newScrollPos = isOutOfRange ? maxScroll : listItemWidth * newIndex;

    $(dragListScrollEl).animate({scrollLeft: newScrollPos}, 500, 'easeOutQuad');
    handleGTMScrollEvent(dragListScrollEl, 'right');
};

/**
 * Handles click on radio inside of list tile
 *
 * @param {Event} e Click event
 */
const onClickRadio = (e) => {
    const el = e.data.currentList;
    $('input[type="radio"]', el).each((i, elInput) => {
        elInput.closest(dragListListItemEl).classList.toggle('selected', elInput.checked);
    });
};

/**
 * Recalculates the scrollbar and slide widths for all sliders on resize
 */
const onResize = () => {
    if (!isTouchDevice()) {
        for (const el of dragListElements) {
            if (el !== 'isMobileOnly') {
                const elScroll = $(el.closest(dragListEl))[0];
                resizeDraglist(elScroll);
            }
        }
    }
};

/**
 * Lookup responsive or overridden value. Return default if not exists.
 *
 * @param {String} prop Name of key in settings
 */
const getProp = (el, prop) => {
    const elData = $(el).data('list-config');
    let propValue = dragListDefaults[prop] || false;

    for (const config of dragListDefaults.responsive) {
        if (config.breakpoint && window.innerWidth < config.breakpoint) {
            propValue = config.settings[prop] || propValue;
        }
    }

    // The manually set properties always overrule so checking that one first
    return (typeof elData !== 'undefined' && typeof elData[prop] !== 'undefined') ? elData[prop] : propValue;
};

/**
 * Calculates the first fully visible list item using the scroll position
 *
 * @param {Number} listIndex The index of the drag list
 */
const getFirstVisibleItem = (listIndex) => {
    const $dragListScrollEl = $(dragListElements[listIndex]);
    const dragListItemWidth = $dragListScrollEl.find(dragListListItemEl).width();
    const scrollLeft = $dragListScrollEl.scrollLeft();

    return scrollLeft > 0 ? Math.ceil(scrollLeft / dragListItemWidth) : scrollLeft;
};

/**
 * Tests if the dragList element exists on the page
 *
 * @returns {Boolean} result of test
 */
const shouldInit = () => {
    return $(dragListEl).length > 0;
};

/**
 * Tests to see if we should prepare the markup
 *
 * @param {HTML Element} el The HTML Element
 * @returns {Boolean} result of test
 */
const shouldPrepareMarkup = (el) => {
    return (getProp(el, 'isDesktopOnly') && !isMobileDevice()) ||
        (getProp(el, 'isMobileOnly') && isMobileDevice()) ||
        (!getProp(el, 'isDesktopOnly') && !getProp(el, 'isMobileOnly'));
};

/**
 * Tests to see if element should be lazyloaded and intersection observer
 * is supported
 *
 * @param {DOM Element} el Draglist element to loazyLoad
 * @returns {Boolean} restult of test
 */
const shouldLazyLoad = (el) => {
    return getProp(el, 'lazyLoad') &&
        'IntersectionObserver' in window &&
        'IntersectionObserverEntry' in window &&
        'intersectionRatio' in window.IntersectionObserverEntry.prototype;
};

/**
 * Pushes horizontal scroll event to datalayer
 * @param {DOM Element} el
 * @param {String} scrollDirection
 * @returns
 */
function handleGTMScrollEvent(el, scrollDirection) {
    const sliderId = $(el).closest('section').data('sliderid');
    const data = {
        'event': 'interaction',
        'interaction_type': 'slider',
        'content_name': sliderId,
        'content_id': sliderId,
        'scroll_direction': scrollDirection
    };

    gtm.pushDataLayerEvent(data);
}

const dragList = {
    init: () => {
        if (shouldInit()) {
            prepareMarkup();
            initDragList();
        }
    }
};

module.exports = dragList;
